import { FeedbackContainer } from "../feedback";
import { DownloadCertIcon, Spinner, ChevronIcon } from "../icons";
import { Speedometr } from "../speedometr";
import { logEvent } from "@amplitude/analytics-browser";
import { createScanReportPDF } from "certificate";
import { Button, SecondaryButton } from "pages/application/Login";
import { OnboardingTooltip} from "../tooltip";
import { useEffect, useState } from "react";
import Switcher from "../Switcher";
import { PermissionGuardModal } from "../PermissionGuardModal";
import { useNavigate } from "react-router-dom";
import {useUser} from "utils";
import {useStores} from "stores/root";
import {observer} from "mobx-react-lite";


export const ResultContainer: React.FC = observer(() => {
    const {input} = useStores()

    if (!input.scannedOn) {
        input.setScannedOn('-')
    }
    return <div className="result-container">
        <div className='result__container'>
            <div className="sections">
                <Switcher options={[{label: 'AI scan', value: 'AI'}, {label: 'Plagiarism', value: 'Plagiarism'}]}
                          value={input.activeSection} onChange={(value: string) => {
                    input.setActiveSection(value as 'AI' | 'Plagiarism');
                }}/>
            </div>
        {
            input.activeSection == 'AI' ?
                <AISection/> :
                <PlagiarismSection/>
        }

        </div>
        <div className="scanned-on">Scanned on: {input.scannedOn}</div>
    </div>
})

export const OpenLine = () => {
    return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.66699 2.6665H4.00033C3.6467 2.6665 3.30756 2.80698 3.05752 3.05703C2.80747 3.30708 2.66699 3.64622 2.66699 3.99984V11.9998C2.66699 12.3535 2.80747 12.6926 3.05752 12.9426C3.30756 13.1927 3.6467 13.3332 4.00033 13.3332H12.0003C12.3539 13.3332 12.6931 13.1927 12.9431 12.9426C13.1932 12.6926 13.3337 12.3535 13.3337 11.9998V9.33317M8.00033 7.99984L13.3337 2.6665M13.3337 2.6665V5.99984M13.3337 2.6665H10.0003" stroke="#CACED9" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
}

const Result: React.FC<{loading: boolean, percents: number, type: 'AI' | 'Plagiarism'}> = ({loading, percents, type}) => {
    return <>
    {
        loading ?
        <div className="spin__container-b">
            <div className="spin">
                <Spinner/>
            </div>
            <div className="spin__container">
                Scanning...
            </div>
        </div> :
        Number.isNaN(percents) || percents === -1 || percents === undefined ?
            <span className='result__percent'>%</span> :
            <span className='result__percent-result'>{percents.toFixed(0)}%</span>

    }
    
    </>
}

const AISection: React.FC = observer(() => {
    const user = useUser()
    const {input} = useStores()
    const {loadingAI: loading, percents} = input
    const certAvailable = input.isCertAvailable()

    const [showReportGuardModal, setShowReportGuardModal] = useState(false);
    const navigate = useNavigate();
    return <>
    <PermissionGuardModal
        open={showReportGuardModal}
        onClose={() => setShowReportGuardModal(false)}
        onUpgrade={() => {
            navigate('/main/subscription');
        }}
        alertText={"Downloading reports is a premium feature."}
    />
    <Result loading={loading} percents={percents} type="AI"/>

        <Speedometr score={Math.max(0, percents)} type="AI"/>
        {
            (Number.isNaN(percents) || percents === -1) && !loading ?
                <span className='result__results'>Run scan to see the results</span> :
                !loading ?
                    percents < 40 ?
                        <div className="spin__container-b human__container-out">
                            <div className="human__container">
                                Human-written
                            </div>
                        </div> :
                        percents < 60 ?
                            <div className="spin__container-b mixed__container-out">
                                <div className="mixed__container">
                                    Mixed
                                </div>
                            </div> :
                            <div className="spin__container-b ai-gen__container">
                                <div className="red-container">
                                    AI-generated
                                </div>
                            </div> :
                    <></>
        }
        {
            !loading && percents !== -1 && !Number.isNaN(percents) && percents >= 0 && percents <= 100 && input.textId !== -1 ?
                <FeedbackContainer score={percents} id={input.textId}/> :
                <></>
        }
        <div style={{marginTop: "auto", display: "flex"}} className={(certAvailable && "primary" || "secondary")}>
        <OnboardingTooltip id={2}>
            {certAvailable ? (
                <Button style={{
                    marginTop: "auto", 
                    display: "flex", 
                    transform: "translateY(-16px)"
                }} className="primary cert-button" onClick={() => {
                    if (!user?.can_create_plagiarism_request) {
                        setShowReportGuardModal(true);
                        return;
                    }
                    logEvent('scan__download_certificate');
                    createScanReportPDF(input.text, percents, 100-percents, !!input.segments.length)
                }}>
                    <DownloadCertIcon/>
                    <div style={{marginLeft: "5px"}}/>Certificate
                </Button>
            ) : (
                <SecondaryButton style={{marginTop: "auto", display: "flex"}} className="secondary cert-button" disabled>
                    <DownloadCertIcon/>
                    <div style={{marginLeft: "5px"}}/>Certificate
                </SecondaryButton>
            )}
        </OnboardingTooltip>
        </div>
    </>;
})

const PlagiarismSection: React.FC = observer(() => {
    const {plagiarism, input, user} = useStores()
    const hasPlagiarismResults = !input.loadingPlagiarism && input.plagiarismData;
    const [showTextHighlight, setShowTextHighlight] = useState(false);

    const plagiarismData = input.plagiarismData as any
    const score = plagiarism.score

    const percents = Number.isNaN(plagiarismData?.plagiarism_score) ? -1 : plagiarismData?.plagiarism_score!


    useEffect(() => {
        if (plagiarismData && plagiarism.groupedResultsSize === 0) {
            plagiarism.setGroupedResults(plagiarismData, input.text);
        }
        user.refresh();
    }, [input.plagiarismData]);


    plagiarism.groupedResults.forEach(result => {
        result.scores.forEach((score: { texts: any[]; }) => {
            console.log(score.texts.join(''), "sentence");
        });
        console.log(result.scores.length, "length");
    });

   return <div className="plagiarism__container">
        <Result loading={input.loadingPlagiarism} percents={score} type="Plagiarism"/>
        <Speedometr score={score} type="Plagiarism"/>
        
        {!hasPlagiarismResults && (
            <span className='result__results'>Run scan to see the results</span>
        )}
        
        {hasPlagiarismResults && (
            <div className="plagiarism-view-toggle">
            </div>
        )}
        
        {hasPlagiarismResults && showTextHighlight ? (
            <div className="plagiarism-highlighted-text" style={{ marginTop: '20px', width: '100%' }}>
            </div>
        ) : (
            <>
                <div className="plagiarism__results-container">
                    {
                        input.loadingPlagiarism ? <>
                        <span className="plagiarism__results">
                            <span className="plagiarism__results-title">Found matches</span> 
                            <span className="plagiarism__results-number"></span>
                        </span>
                        <BlockLoading />
                        <BlockLoading />
                        </> : hasPlagiarismResults ? (
                            <span className="plagiarism__results">
                                <span className="plagiarism__results-title">Found matches</span> 
                                <span className="plagiarism__results-number">{plagiarism.groupedResults.length}</span>
                            </span>
                        ) : null
                    }
                </div>
                
                {hasPlagiarismResults && (
                    <div className="blocks-list">
                        {plagiarism.groupedResults.map((result, index) => (
                            <Block 
                                key={index}
                                title={result.title} 
                                link={result.shortLink} 
                                probability={result.score}
                                scores={result.scores}
                                originalUrl={result.link}
                            />
                        ))}
                    </div>
                )}
            </>
        )}
        
        {!hasPlagiarismResults && !input.loadingPlagiarism && (
            <div className="blocks-list">
                <span style={{color: '#5C5E64', fontFamily: 'Satoshi-Regular', fontSize: '14px'}}>
                </span>
            </div>
        )}
    </div>
})

const BlockLoading: React.FC = ({
}) => {
    return (
        <div className="block">
            <div style={{width: 52, height: 52, borderRadius: "16px", backgroundColor: "#EEF0F2"}}></div>
            <div style={{display: "flex", flexDirection: "column", justifyContent: 'space-around'}}>
                <div style={{width: 180, height: 12, borderRadius: "16px", backgroundColor: "#EEF0F2"}}></div>
                <div style={{width: 90, height: 12, borderRadius: "16px", backgroundColor: "#EEF0F2"}}></div>
            </div>
        </div>
    );
}

const Block: React.FC<{
    title: string, 
    link: string, 
    probability: number,
    scores: Array<{probability: number, texts: string[], shortLink: string}>
    originalUrl: string
}> = ({title, link, probability, scores, originalUrl}) => {
    const [open, setOpen] = useState(false);

    const truncatedTitle = title.length > 40 ? title.slice(0, 40) + '...' : title;
    const truncatedLink = link.length > 30 ? link.slice(0, 30) + '...' : link;

    return (
        <div className="block-plagiarism">
            <div className="block-plagiarism__header">
                <div className="block-plagiarism__content">
                    <div style={{
                        width: 52, 
                        height: 52, 
                        borderRadius: "16px", 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        backgroundColor: "white",
                        border: "1px solid #EDF1F9",
                        color: "#080C1E",
                    }}>{probability}%</div>
                    <div style={{
                        display: "flex", 
                        flexDirection: "column", 
                        justifyContent: 'center',
                        gap: 2
                    }}>
                        <span style={{
                            borderRadius: "16px",
                            fontFamily: "Satoshi-Regular!important",
                            marginBottom: 0
                        }} className="block__title">{truncatedTitle}</span>
                        <span style={{display: "flex", alignItems: "center", gap: 4, cursor: "pointer"}} onClick={() => {
                            window.open(originalUrl, '_blank');
                        }}>
                            <span className="block__link">{truncatedLink}</span>
                            <OpenLine/>
                        </span>
                    </div>
                </div>
                <div 
                    style={{transform: `rotate(${open ? 90 : -90}deg)`, cursor: "pointer", width: "20px", height: "20px"}} 
                    onClick={() => setOpen(!open)}
                >
                    <ChevronIcon/>
                </div>
            </div>
            {
                open && scores.map((score, index) => (
                    <SubBlock key={index} probability={Math.round(score.probability * 100)} texts={score.texts} shortLink={score.shortLink} index={index}/>
                ))
            }
        </div>
    );
}


const SubBlock: React.FC<{probability: number, texts: string[], shortLink: string, index: number}> = observer(({probability, texts, shortLink, index}) => {
    const shouldTruncate = texts.join(' ').length > 100;
    const displayText = !shouldTruncate ? texts.join(' ') : texts.join(' ').slice(0, 100) + '...';
    const {plagiarism} = useStores()

    return (
        <div className="subblock" 
        onMouseEnter={() => {
            plagiarism.setSelectedSentences(texts);
            console.log(texts, "text");
        }}
        onMouseLeave={() => {
            plagiarism.setSelectedSentences([]);
        }}
        onClick={() => {
            console.log(index, "index");
            plagiarism.setActiveSubgroup(index + 1);
            plagiarism.setActiveGroup(shortLink);
        }}
        >
            <div style={{
                width: 52, 
                height: 52, 
                borderRadius: "16px", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                backgroundColor: "white",
                border: "1px solid #EDF1F9",
                color: "black",
            }}>{probability}%</div>
            <div style={{
                display: "flex", 
                flexDirection: "column", 
                justifyContent: 'center',
                gap: 2
            }}>
                <span style={{
                    borderRadius: "16px",
                    fontFamily: "Satoshi-Regular!important",
                    marginBottom: 0,
                    maxWidth: "240px"
                }} className="block__title">
                    {displayText} 
                </span>
            </div>
        </div>
    );
})